import React from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { Icon } from "../Icon";

type BaseDialogHeaderProps = {
  title?: string;
  onClose?: () => void;
  hideCloseIcon?: boolean;
  titleAlign?: "left" | "center";
  headerContent?: React.ReactNode;
};

type WithBackArrow = BaseDialogHeaderProps & {
  hasBackArrow: true;
  onBack: () => void;
};

type WithoutBackArrow = BaseDialogHeaderProps & {
  hasBackArrow?: false;
  onBack?: never;
};

/**
 * DialogHeader
 *
 * Header area for dialogs with optional back arrow.
 */
export type DialogHeaderProps = WithBackArrow | WithoutBackArrow;

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  onClose,
  hideCloseIcon = false,
  titleAlign = "left",
  hasBackArrow = false,
  onBack,
  headerContent,
}) => {
  return (
    <div
      className={twMerge(
        "relative flex h-16 w-full flex-shrink-0 items-center justify-between border-b px-6",
        title && "border-b",
        titleAlign === "center" ? "justify-center" : ""
      )}
    >
      {hasBackArrow && (
        <Button variant="icon" onPress={onBack} className="absolute left-6" aria-label="Go back">
          <Icon name="CaretLeft" data-testid="back-arrow" />
        </Button>
      )}
      {headerContent || (
        <>
          {title && <Heading size="xs">{title}</Heading>}
          {onClose && !hideCloseIcon && (
            <Button variant="icon" onPress={onClose} aria-label="Close dialog">
              <Icon name="X" data-testid="close-button" />
            </Button>
          )}
        </>
      )}
    </div>
  );
};
